/**
 * Token Refresh Service
 *
 * Centralized service for managing OKTA token refresh operations.
 * Features:
 * - Queue management to prevent multiple concurrent refresh requests
 * - Pending requests notification
 * - Retry with exponential backoff for network errors
 * - Error categorization and automatic logout on token expiration
 * - Comprehensive logging for debugging and monitoring
 *
 * Aligned with mobile implementation for consistency.
 */

import { datadogLogs } from "@datadog/browser-logs";

import { useAuthStore } from "src/stores/auth-store";
import { logger } from "src/utils";
import {
  createRefreshTokenError,
  isNetworkError,
  RefreshTokenError,
} from "src/utils/okta/token-errors";
import type { RefreshTokenErrorType } from "src/utils/okta/token-errors";

import { oktaAuthService } from "./oktaAuth.service";

// Re-export for convenience
export { RefreshTokenError };
export type { RefreshTokenErrorType };

/** Maximum retry attempts for network errors */
const MAX_RETRY_ATTEMPTS = 3;
/** Initial delay for exponential backoff (1 second) */
const INITIAL_RETRY_DELAY_MS = 1000;

/**
 * Token response from refresh operation
 */
export interface OktaTokens {
  accessToken: string;
  expiresAt?: number;
}

/**
 * Queue of pending requests waiting for refresh to complete
 */
type PendingRequest = {
  resolve: (tokens: OktaTokens) => void;
  reject: (error: Error) => void;
};

/**
 * Sleep for a given duration
 */
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Log to both console logger and Datadog for critical events
 */
const logCriticalEvent = (message: string, context?: Record<string, unknown>): void => {
  logger.error(message);
  try {
    datadogLogs.logger.error(message, context);
  } catch {
    // Datadog may not be initialized
  }
};

/**
 * Token Refresh Service
 *
 * Manages token refresh with:
 * - Single refresh at a time (queue management)
 * - Pending requests queue for concurrent callers
 * - Retry with exponential backoff for network errors
 * - Automatic logout on refresh token expiration
 * - Proper error categorization
 */
class TokenRefreshService {
  /** Promise of the current refresh operation, null if no refresh in progress */
  private refreshPromise: Promise<OktaTokens> | null = null;

  /** Queue of pending requests waiting for the current refresh */
  private pendingRequests: Array<PendingRequest> = [];

  /**
   * Refresh tokens with queue management
   *
   * If a refresh is already in progress, queues the request and waits
   * for the current refresh to complete.
   *
   * @returns Promise<OktaTokens> - The new tokens
   * @throws RefreshTokenError if refresh fails
   */
  async refreshTokens(): Promise<OktaTokens> {
    // If a refresh is already in progress, queue this request
    if (this.refreshPromise) {
      logger.info("[TokenRefresh] Refresh already in progress, queuing request");
      return new Promise<OktaTokens>((resolve, reject) => {
        this.pendingRequests.push({ resolve, reject });
      });
    }

    logger.info("[TokenRefresh] Starting new token refresh...");

    // Start the refresh with retry logic
    this.refreshPromise = TokenRefreshService.executeRefreshWithRetry();

    try {
      const tokens = await this.refreshPromise;
      // Notify all pending requests with the new tokens
      this.notifyPendingRequests(tokens);
      return tokens;
    } catch (error) {
      // Notify all pending requests of the error
      this.notifyPendingRequestsError(error as Error);
      throw error;
    } finally {
      // Reset to allow new refresh
      this.refreshPromise = null;
      this.pendingRequests = [];
    }
  }

  /**
   * Execute token refresh with retry logic for network errors
   */
  private static async executeRefreshWithRetry(): Promise<OktaTokens> {
    let lastError: RefreshTokenError | null = null;
    let attempt = 0;

    while (attempt < MAX_RETRY_ATTEMPTS) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await TokenRefreshService.executeRefresh();
      } catch (error) {
        const refreshError =
          error instanceof RefreshTokenError ? error : createRefreshTokenError(error);
        lastError = refreshError;

        // Only retry on network errors
        if (!isNetworkError(error)) {
          throw refreshError;
        }

        // Check if we've exhausted retries
        if (attempt === MAX_RETRY_ATTEMPTS - 1) {
          logger.error(
            `[TokenRefresh] Network error persisted after ${MAX_RETRY_ATTEMPTS} attempts`
          );
          logCriticalEvent("[TokenRefresh] Network error persisted, performing logout", {
            attempts: MAX_RETRY_ATTEMPTS,
            error: refreshError.message,
          });

          // Logout after all retries exhausted
          const { logout } = useAuthStore.getState();
          // eslint-disable-next-line no-await-in-loop
          await logout();
          throw refreshError;
        }

        const delay = INITIAL_RETRY_DELAY_MS * 2 ** attempt;
        const currentAttempt = attempt + 1;
        logger.info(
          `[TokenRefresh] Network error, retrying in ${delay}ms (attempt ${currentAttempt}/${MAX_RETRY_ATTEMPTS})`
        );
        // eslint-disable-next-line no-await-in-loop
        await sleep(delay);
        attempt += 1;
      }
    }

    // Should not reach here, but TypeScript needs this
    throw lastError ?? new Error("Token refresh failed");
  }

  /**
   * Execute the actual token refresh (single attempt)
   */
  private static async executeRefresh(): Promise<OktaTokens> {
    const { isOktaLoginEnabled, logout } = useAuthStore.getState();

    // Verify user is authenticated via OKTA
    if (!isOktaLoginEnabled) {
      logger.info("[TokenRefresh] User is not authenticated via OKTA, refresh token not available");
      throw createRefreshTokenError(
        new Error("User is not authenticated via OKTA, refresh token not available")
      );
    }

    logger.info("[TokenRefresh] Executing token refresh via OKTA TokenManager");

    try {
      const result = await oktaAuthService.refreshAccessToken();

      if (result.success && result.token && "accessToken" in result.token) {
        logger.info("[TokenRefresh] Token refresh successful");

        const tokens: OktaTokens = {
          accessToken: result.token.accessToken,
          expiresAt: result.token.expiresAt,
        };

        return tokens;
      }

      // Refresh failed - throw typed error
      throw createRefreshTokenError(new Error(result.error ?? "Token refresh failed"));
    } catch (error) {
      // If already a RefreshTokenError, use it directly
      const refreshError =
        error instanceof RefreshTokenError ? error : createRefreshTokenError(error);

      logger.error(
        `[TokenRefresh] Token refresh failed: ${refreshError.type} - ${refreshError.message}`
      );

      // Handle based on error type
      switch (refreshError.type) {
        case "REFRESH_TOKEN_EXPIRED":
          logCriticalEvent("[TokenRefresh] Refresh token expired, performing logout", {
            errorType: refreshError.type,
          });
          await logout();
          throw refreshError;

        case "REFRESH_TOKEN_INVALID":
          logCriticalEvent("[TokenRefresh] Refresh token invalid, performing logout", {
            errorType: refreshError.type,
          });
          await logout();
          throw refreshError;

        case "NETWORK_ERROR":
          // Network errors are handled by retry logic in executeRefreshWithRetry
          logger.error(`[TokenRefresh] Network error during refresh: ${refreshError.message}`);
          throw refreshError;

        default:
          // Unknown error - info level (includes expected legacy auth case)
          logger.info(
            `[TokenRefresh] Unknown error during refresh: ${refreshError.type} - ${refreshError.message}`
          );
          throw refreshError;
      }
    }
  }

  /**
   * Notify all pending requests with the new tokens
   */
  private notifyPendingRequests(tokens: OktaTokens): void {
    if (this.pendingRequests.length > 0) {
      logger.info(`[TokenRefresh] Notifying ${this.pendingRequests.length} pending requests`);
      this.pendingRequests.forEach(({ resolve }) => {
        resolve(tokens);
      });
    }
  }

  /**
   * Notify all pending requests of an error
   */
  private notifyPendingRequestsError(error: Error): void {
    if (this.pendingRequests.length > 0) {
      logger.error(
        `[TokenRefresh] Notifying ${this.pendingRequests.length} pending requests of error`
      );
      this.pendingRequests.forEach(({ reject }) => {
        reject(error);
      });
    }
  }

  /**
   * Check if a refresh operation is currently in progress
   */
  isRefreshing(): boolean {
    return this.refreshPromise !== null;
  }
}

/** Singleton instance of the token refresh service */
export const tokenRefreshService = new TokenRefreshService();

/**
 * Helper function to refresh tokens
 * Uses the singleton service
 */
export const refreshTokens = async (): Promise<OktaTokens> => {
  return tokenRefreshService.refreshTokens();
};
